import {CheckCircleIcon, CodeReviewIcon, CommentDiscussionIcon} from '@primer/octicons-react'
import {Blankslate} from '@primer/react/experimental'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {useLayoutAuthorLogin} from '../../stores/layout-selectors'
import styles from './CommentsEmptyState.module.css'

export type CommentsEmptyStateVariant =
  /** No issue comments or reviews exist. */
  | 'conversation'
  /** No review comments exist. */
  | 'review-comments'
  /** Review comments exist, but they have all been resolved. */
  | 'all-resolved'
  /** The current user has not left any review comments. */
  | 'mine'

const variants = (viewerIsAuthor: boolean) => ({
  conversation: {
    icon: CommentDiscussionIcon,
    heading: 'No comments yet',
    description: 'Add a comment to start the conversation.',
  },
  'review-comments': {
    icon: CodeReviewIcon,
    heading: 'No review comments yet',
    description: viewerIsAuthor
      ? 'Request a review to get feedback on your changes.'
      : 'Review and add comments to share your feedback.',
  },
  'all-resolved': {
    icon: CheckCircleIcon,
    heading: 'All comments resolved',
    description: 'All review comments have been resolved. Nice work!',
  },
  mine: {
    icon: CodeReviewIcon,
    heading: "You haven't left any review comments yet",
    description: 'Review and add comments to share your feedback.',
  },
})

export function CommentsEmptyState({variant = 'conversation'}: {variant?: CommentsEmptyStateVariant}) {
  const viewerIsAuthor = useLayoutAuthorLogin() === getCurrentUserLogin()
  const {icon: Icon, heading, description} = variants(viewerIsAuthor)[variant]

  return (
    <div className="d-flex flex-column position-relative width-full height-full flex-justify-center">
      <Blankslate className={styles.blankslate}>
        <Blankslate.Visual>
          <Icon size="medium" />
        </Blankslate.Visual>
        <Blankslate.Heading>{heading}</Blankslate.Heading>
        {description && <Blankslate.Description>{description}</Blankslate.Description>}
      </Blankslate>
    </div>
  )
}
